import React, { useState } from 'react';
import { useOnboarding } from '../contexts/OnboardingContext';

const STEPS = [
  { label: 'Welkom' },
  { label: 'Documenten' },
  { label: 'AI Instructies' },
  { label: "Video's" },
  { label: 'Klaar' },
];

const TOTAL = STEPS.length;

// ─── Progress bubbles ──────────────────────────────────────────────────────────

function ProgressBubbles({ current }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center', gap: 8, marginBottom: 28 }}>
      {STEPS.map((s, i) => {
        const isActive = i === current;
        const isDone = i < current;
        return (
          <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5 }}>
            <div style={{
              width: isActive ? 32 : 24,
              height: isActive ? 32 : 24,
              borderRadius: '50%',
              background: isActive ? 'var(--brand, #2563eb)' : isDone ? '#93c5fd' : '#e5e7eb',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: isActive || isDone ? 'white' : '#9ca3af',
              fontSize: isActive ? 13 : 11,
              fontWeight: 600,
              transition: 'all 0.2s ease',
              flexShrink: 0,
            }}>
              {isDone ? '✓' : i + 1}
            </div>
            <span style={{
              fontSize: 10, fontWeight: 600, whiteSpace: 'nowrap',
              color: isActive ? 'var(--brand, #2563eb)' : '#9ca3af',
              opacity: isActive ? 1 : 0,
              transition: 'opacity 0.2s',
            }}>
              {s.label}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ─── Step contents ─────────────────────────────────────────────────────────────

function StepWelcome() {
  return (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: '#111', margin: '0 0 10px' }}>
        Welkom bij TravelAIgent 👋
      </h2>
      <p style={{ color: '#6b7280', fontSize: 14, lineHeight: 1.65, margin: '0 0 18px' }}>
        TravelAIgent is uw persoonlijke AI-assistent voor uw reisbureau. Het helpt u bij:
      </p>
      <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 18px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {[
          'Razendsnel reisvoorstellen opstellen voor klanten',
          'Veelgestelde vragen automatisch beantwoorden',
          'Nieuwsbrieven en reisbescheiden genereren',
          'Klantcommunicatie verbeteren met AI',
        ].map((item, i) => (
          <li key={i} style={{ display: 'flex', gap: 10, fontSize: 14, color: '#374151' }}>
            <span style={{ color: '#2563eb', fontWeight: 700, flexShrink: 0 }}>→</span>
            {item}
          </li>
        ))}
      </ul>
      <p style={{ color: '#6b7280', fontSize: 13, lineHeight: 1.65, margin: 0 }}>
        In de volgende stappen richten we uw account in: we uploaden uw bestaande documenten,
        stellen de AI-instructies in en laten u de belangrijkste functies zien.
      </p>
    </div>
  );
}

function StepDocuments() {
  const [files, setFiles] = useState([]);
  const [dragging, setDragging] = useState(false);

  const addFiles = (fileList) => setFiles(prev => [...prev, ...Array.from(fileList)]);

  return (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: '#111', margin: '0 0 8px' }}>
        Voeg bestaande documenten toe
      </h2>
      <p style={{ color: '#6b7280', fontSize: 14, lineHeight: 1.65, margin: '0 0 20px' }}>
        Upload reisdocumenten, brochures of informatie die uw AI-assistent kan gebruiken.
      </p>
      <div
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={e => { e.preventDefault(); setDragging(false); addFiles(e.dataTransfer.files); }}
        onClick={() => document.getElementById('ob-file-input').click()}
        style={{
          border: `2px dashed ${dragging ? '#2563eb' : '#d1d5db'}`,
          borderRadius: 10, padding: '28px 20px', textAlign: 'center',
          cursor: 'pointer', background: dragging ? '#eff6ff' : '#fafafa',
          transition: 'all 0.2s', marginBottom: 14,
        }}
      >
        <div style={{ fontSize: 26, marginBottom: 8 }}>📂</div>
        <p style={{ color: '#374151', fontWeight: 500, fontSize: 14, margin: '0 0 4px' }}>
          Sleep bestanden hierheen of klik om te uploaden
        </p>
        <p style={{ color: '#9ca3af', fontSize: 12, margin: 0 }}>PDF, Word, Excel — max 10 MB per bestand</p>
        <input
          id="ob-file-input" type="file" multiple
          accept=".pdf,.doc,.docx,.xls,.xlsx,.txt"
          style={{ display: 'none' }}
          onChange={e => addFiles(e.target.files)}
        />
      </div>
      {files.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
          {files.map((f, i) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '7px 12px', background: '#f0f9ff',
              border: '1px solid #bae6fd', borderRadius: 6, fontSize: 13,
            }}>
              <span>📄</span>
              <span style={{ flex: 1, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {f.name}
              </span>
              <button
                onClick={() => setFiles(prev => prev.filter((_, j) => j !== i))}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 18, lineHeight: 1, padding: 0 }}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
      <p style={{ fontSize: 12, color: '#9ca3af', margin: 0 }}>
        U kunt ook later documenten toevoegen via de Kennisbank in het menu.
      </p>
    </div>
  );
}

function StepAIInstructions() {
  const { aiInstructions, saveAiInstructions } = useOnboarding();
  const [val, setVal] = useState(aiInstructions);

  return (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: '#111', margin: '0 0 8px' }}>
        AI-instructies instellen
      </h2>
      <p style={{ color: '#6b7280', fontSize: 14, lineHeight: 1.65, margin: '0 0 20px' }}>
        Vertel uw AI-assistent hoe hij moet communiceren. Denk aan toon, stijl en specifieke richtlijnen.
      </p>
      <textarea
        value={val}
        onChange={e => setVal(e.target.value)}
        onBlur={() => saveAiInstructions(val)}
        placeholder="Bijv: Communiceer altijd professioneel en vriendelijk. Gebruik 'u' als aanspreking. Vermeld altijd dat annuleringsvoorwaarden van toepassing zijn bij aanbiedingen..."
        style={{
          width: '100%', minHeight: 150, padding: 14, boxSizing: 'border-box',
          border: '1px solid #d1d5db', borderRadius: 8,
          fontSize: 14, color: '#374151', lineHeight: 1.6,
          resize: 'vertical', fontFamily: 'inherit',
          outline: 'none',
        }}
      />
      <p style={{ fontSize: 12, color: '#9ca3af', margin: '8px 0 0' }}>
        U kunt dit later aanpassen via AI Instructies in het menu.
      </p>
    </div>
  );
}

function StepVideos() {
  const videos = [
    { title: 'Reisvoorstellen maken', desc: 'In seconden een professioneel reisvoorstel genereren', duration: '3:45' },
    { title: 'AI Chat gebruiken', desc: 'Efficiënter communiceren met uw AI-assistent', duration: '4:12' },
    { title: 'Nieuwsbrief opstellen', desc: 'Automatisch gepersonaliseerde nieuwsbrieven maken', duration: '3:20' },
    { title: 'Boekingen beheren', desc: 'Overzicht van boekingen en reisbescheiden', duration: '2:58' },
  ];

  return (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: '#111', margin: '0 0 8px' }}>
        Instructievideo's
      </h2>
      <p style={{ color: '#6b7280', fontSize: 14, lineHeight: 1.65, margin: '0 0 20px' }}>
        Bekijk de korte video's om snel aan de slag te gaan met de belangrijkste functies.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        {videos.map((v, i) => (
          <div key={i} style={{ border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden', cursor: 'pointer' }}>
            <div style={{
              background: '#f3f4f6', height: 90,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              position: 'relative',
            }}>
              <div style={{
                width: 36, height: 36, borderRadius: '50%',
                background: 'rgba(0,0,0,0.12)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <div style={{
                  width: 0, height: 0,
                  borderTop: '7px solid transparent',
                  borderBottom: '7px solid transparent',
                  borderLeft: '12px solid #4b5563',
                  marginLeft: 3,
                }} />
              </div>
              <span style={{
                position: 'absolute', bottom: 6, right: 8,
                fontSize: 11, color: '#6b7280', background: 'white',
                padding: '2px 5px', borderRadius: 4,
              }}>
                {v.duration}
              </span>
            </div>
            <div style={{ padding: '10px 12px' }}>
              <p style={{ fontSize: 13, fontWeight: 600, color: '#111', margin: '0 0 3px' }}>{v.title}</p>
              <p style={{ fontSize: 12, color: '#9ca3af', lineHeight: 1.4, margin: 0 }}>{v.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function StepComplete({ onFinish }) {
  return (
    <div style={{ textAlign: 'center', padding: '16px 0 8px' }}>
      <div style={{ fontSize: 48, marginBottom: 16 }}>🎉</div>
      <h2 style={{ fontSize: 22, fontWeight: 700, color: '#111', margin: '0 0 12px' }}>
        Onboarding voltooid!
      </h2>
      <p style={{ color: '#6b7280', fontSize: 14, lineHeight: 1.7, margin: '0 auto 28px', maxWidth: 380 }}>
        Uw TravelAIgent-account is ingesteld. U kunt alle functies nu gebruiken.
        Via het vraagteken-icoon in de topbalk kunt u de onboarding altijd opnieuw bekijken.
      </p>
      <button
        onClick={onFinish}
        style={{
          padding: '12px 32px', background: 'var(--brand, #2563eb)',
          color: 'white', border: 'none', borderRadius: 8,
          fontWeight: 600, fontSize: 15, cursor: 'pointer',
        }}
      >
        Naar het dashboard →
      </button>
    </div>
  );
}

// ─── Main overlay ──────────────────────────────────────────────────────────────

export default function OnboardingOverlay() {
  const { overlayVisible, step, goToStep, skip, complete } = useOnboarding();
  if (!overlayVisible) return null;

  const isLast = step === TOTAL - 1;

  const handleNext = async () => {
    if (isLast) {
      await complete();
    } else {
      await goToStep(step + 1);
    }
  };

  const renderStep = () => {
    switch (step) {
      case 0: return <StepWelcome />;
      case 1: return <StepDocuments />;
      case 2: return <StepAIInstructions />;
      case 3: return <StepVideos />;
      case 4: return <StepComplete onFinish={complete} />;
      default: return null;
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 900,
      backgroundColor: 'rgba(0,0,0,0.5)',
      backdropFilter: 'blur(8px)',
      WebkitBackdropFilter: 'blur(8px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 24,
    }}>
      {/* Card + skip arrow row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, maxWidth: 632, width: '100%' }}>

        {/* Card */}
        <div style={{
          background: 'white', borderRadius: 16, flex: 1,
          maxHeight: '90vh', overflow: 'hidden',
          boxShadow: '0 25px 60px rgba(0,0,0,0.25)',
          display: 'flex', flexDirection: 'column',
        }}>
          {/* Progress */}
          <div style={{ padding: '28px 32px 0' }}>
            <ProgressBubbles current={step} />
          </div>

          {/* Step content */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '0 32px 24px' }}>
            {renderStep()}
          </div>

          {/* Footer nav */}
          {step < TOTAL - 1 && (
            <div style={{
              padding: '14px 32px', borderTop: '1px solid #f3f4f6',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              flexShrink: 0,
            }}>
              <button
                onClick={skip}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: '#9ca3af', fontSize: 14, padding: '8px 0',
                }}
              >
                Overslaan
              </button>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {step > 0 && (
                  <button
                    onClick={() => goToStep(step - 1)}
                    style={{
                      padding: '9px 18px', background: 'transparent',
                      color: '#374151', border: '1px solid #e5e7eb',
                      borderRadius: 8, fontWeight: 500, fontSize: 14, cursor: 'pointer',
                    }}
                  >
                    ← Vorige
                  </button>
                )}
                <button
                  onClick={handleNext}
                  style={{
                    padding: '9px 20px', background: 'var(--brand, #2563eb)',
                    color: 'white', border: 'none', borderRadius: 8,
                    fontWeight: 600, fontSize: 14, cursor: 'pointer',
                  }}
                >
                  {step === TOTAL - 2 ? 'Voltooien' : 'Volgende →'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Skip arrow — right of card */}
        {step < TOTAL - 1 && (
          <button
            onClick={skip}
            title="Onboarding overslaan"
            style={{
              background: 'rgba(255,255,255,0.15)',
              border: '1px solid rgba(255,255,255,0.25)',
              borderRadius: 8, padding: '10px 12px', cursor: 'pointer',
              color: 'rgba(255,255,255,0.8)', fontSize: 14, fontWeight: 700,
              letterSpacing: 1, flexShrink: 0,
              backdropFilter: 'blur(4px)',
              WebkitBackdropFilter: 'blur(4px)',
              transition: 'background 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.25)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
          >
            -&gt;&gt;
          </button>
        )}
      </div>
    </div>
  );
}
